import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SalesBillBuilder } from "@/components/sales/SalesBillBuilder";
import TallyUpload from "@/pages/TallyUpload";
import { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Plus, Search, FileText, ChevronRight } from "lucide-react";

function BillsList() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useResource("/sales-bills");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [q, setQ] = useState("");

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    return `${r.billNo} ${r.buyerName} ${r.buyerId} ${r.status}`.toLowerCase().includes(q.toLowerCase());
  }), [rows, q]);

  return (
    <div className="space-y-5">
      <PageHeader title="Sales Bills" subtitle={`${rows.length} bills · draft → confirm → dispatch`}>
        <Button data-testid="btn-new-sales-bill" onClick={() => setBuilderOpen(true)} className="gap-2 bg-amber-700 hover:bg-amber-800">
          <Plus className="h-4 w-4" /> New Sales Bill
        </Button>
      </PageHeader>

      <div className="relative w-full sm:w-96">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input data-testid="sales-bill-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search bill / buyer / status…" className="h-9 bg-white pl-8 text-sm" />
      </div>

      {isLoading ? <LoadingState label="Loading sales bills…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        filtered.length === 0 ? (
          <EmptyState title="No sales bills yet" message="Create a sales bill — pick a buyer, add products with buyer-specific pricing, then confirm & dispatch."
            action={<Button data-testid="empty-new-sales-bill" onClick={() => setBuilderOpen(true)} className="gap-2 bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4" /> New Sales Bill</Button>} />
        ) : (
          <div className="space-y-2.5">
            {filtered.map((r) => (
              <button key={r.id} data-testid={`sales-bill-row-${r.id}`} onClick={() => navigate(`/sales-bills/${r.id}`)}
                className="flex w-full items-center justify-between gap-4 rounded-xl border border-stone-200 bg-white px-5 py-3.5 text-left shadow-sm transition-colors hover:border-amber-300 hover:bg-amber-50/30">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-stone-100 p-2"><FileText className="h-4 w-4 text-stone-500" /></div>
                  <div>
                    <div className="font-mono text-sm font-semibold text-stone-900">{r.billNo}</div>
                    <div className="text-xs text-stone-500">{r.buyerName} · {fmtDate(r.billDate)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-sm font-bold text-stone-800">{fmtCurrency(r.grandTotal)}</span>
                  <StatusBadge value={r.status} />
                  <ChevronRight className="h-4 w-4 text-stone-300" />
                </div>
              </button>
            ))}
          </div>
        )}

      <SalesBillBuilder open={builderOpen} onOpenChange={setBuilderOpen} onCreated={(bill) => { refetch(); if (bill?.id) navigate(`/sales-bills/${bill.id}`); }} />
    </div>
  );
}

export default function SalesBills() {
  const [tab, setTab] = useState("list");
  return (
    <Tabs value={tab} onValueChange={setTab} className="space-y-4">
      <TabsList data-testid="salesbills-tabs" className="bg-stone-100">
        <TabsTrigger value="list" data-testid="salesbills-tab-list">Sales Bills</TabsTrigger>
        <TabsTrigger value="tally" data-testid="salesbills-tab-tally">Tally Import</TabsTrigger>
      </TabsList>
      <TabsContent value="list"><BillsList /></TabsContent>
      <TabsContent value="tally"><TallyUpload /></TabsContent>
    </Tabs>
  );
}
